// ResizeHandle.tsx — Draggable divider between panes (like VS Code sash)
// Renders a thin vertical bar. On mousedown, tracks mouse delta and calls
// onResize(delta) on every mousemove until mouseup.

import { useCallback, useRef } from 'react';

interface Props {
  onResize: (deltaX: number) => void;
}

export default function ResizeHandle({ onResize }: Props) {
  const startX = useRef(0);
  const active = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    startX.current = e.clientX;
    active.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!active.current) return;
    const dx = e.clientX - startX.current;
    startX.current = e.clientX;
    onResize(dx);
  }, [onResize]);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    active.current = false;
    try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* */ }
  }, []);

  return (
    <div
      className="resize-handle"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    />
  );
}
